import { OFFSET_ITEM_HEIGHT } from '@issuemanager/apps/client/utils';
import {
  AutocompleteGroupedOption,
  Box,
  Checkbox,
  CircularProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  Typography,
  useAutocomplete,
  useControlled,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { unstable_useEventCallback as useEventCallback } from '@mui/utils';
import React, { useRef, useState, type JSX } from 'react';
import { TextField } from '../../text-field';
import { useExtendedAutocompleteDefaultProps } from '../hooks/use-defaultized-props';
import { useKeyboardNavigation } from '../hooks/use-keyboard-navigation';
import type { ExtendedAutocompleteProps, RenderOptionState } from '../types';
import { getBooleanValueFromAriaAttribute } from '../utils';
import { ExtendedAutocompleteActions } from './actions';

const ListContainer = styled('div')(
  ({ theme }) => `
  position: absolute;
  background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
  border-radius: 0 0 4px 4px;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
    0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  z-index: 1;

  & ul {
    margin: 0;
    padding: 0;
    list-style: none;
    overflow: auto;
    max-height: 250px;
  }
`,
);

export function ExtendedAutocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: ExtendedAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
): JSX.Element {
  const {
    // UI-related props
    loading,
    TextFieldProps,
    ClearButtonProps,
    ApplyButtonProps,
    CancelButtonProps,
    listboxProps,
    noOptionsText,
    renderOption: renderOptionProp,
    renderCheckbox: renderCheckboxProp,
    groupBy,
    onClear,
    onApply,
    onClose: onCloseProp,
    onScrollEnd,
    // Logic-related props
    open: openProp,
    inputValue: inputValueProp,
    multiple,
    getOptionLabel,
    getTestIdOption,
    maxLength,
    readOnly,
    ...rest
  } = useExtendedAutocompleteDefaultProps(props);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useControlled({
    controlled: openProp,
    default: false,
    name: 'ExtendedAutocompleteOpenState',
  });
  const [inputValue, setInputValue] = useControlled({
    controlled: inputValueProp,
    default: '',
    name: 'ExtendedAutocompleteInputValue',
  });

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete<T, Multiple, DisableClearable, FreeSolo>({
    ...rest,
    open,
    inputValue,
    multiple,
    getOptionLabel,
    groupBy,
    onInputChange: (event, value, reason) => {
      if (multiple && reason === 'reset') return;
      setInputValue(value);
      rest.onInputChange?.(event, value, reason);
    },
    onClose: (event, reason) => {
      if (multiple && reason !== 'blur') return;
      if (reason === 'blur') {
        return;
      }

      onCloseProp?.(event, reason);
      setOpen(false);
    },
    onOpen: (event) => {
      if (readOnly) return;
      rest.onOpen?.(event);
      setOpen(true);
    },
  });

  const { optionRefs, handleOptionKeyDown, handleInputKeyDown } =
    useKeyboardNavigation({
      groupedOptions,
      anchorEl,
      getOptionProps,
      groupBy,
    });

  const handleClear = useEventCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setInputValue('');
      onClear?.();
      ClearButtonProps?.onClick?.(event);
      setOpen(false);
    },
  );

  const handleApply = useEventCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setInputValue('');
      onApply?.();
      setOpen(false);
      ApplyButtonProps?.onClick?.(event);
    },
  );

  const handleCancel = useEventCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setInputValue('');
      // NOTE: we can consider blur as a cancel action
      // since the user is losing focus on this element
      onCloseProp?.(event, 'blur');
      setOpen(false);
      CancelButtonProps?.onClick?.(event);
    },
  );

  const handleScroll = useEventCallback(
    (event: React.UIEvent<HTMLUListElement, UIEvent>) => {
      const { offsetHeight, scrollTop, scrollHeight } = event.currentTarget;
      if (offsetHeight + scrollTop >= scrollHeight - OFFSET_ITEM_HEIGHT) {
        onScrollEnd?.();
        listboxProps?.onScroll?.(event);
      }
    },
  );

  const defaultRenderOption = (
    _: React.HTMLAttributes<HTMLLIElement>,
    option: T,
  ): JSX.Element => (
    <ListItemText
      primary={getOptionLabel(option)}
      data-testid={`extended-autocomplete-${
        getTestIdOption?.(option) ?? 'list-item'
      }`}
    />
  );

  const defaultRenderCheckbox = (
    _: T,
    state: RenderOptionState,
  ): JSX.Element => (
    <Checkbox
      {...state}
      tabIndex={-1}
      disableRipple
      sx={{ pointerEvents: 'none' }}
    />
  );

  const renderOption = renderOptionProp ?? defaultRenderOption;
  const renderCheckbox = renderCheckboxProp ?? defaultRenderCheckbox;

  const renderListOption = (option: T, index: number): JSX.Element => {
    const optionProps = getOptionProps({ option, index });
    const ariaSelected = optionProps['aria-selected'];
    const ariaDisabled = optionProps['aria-disabled'];
    const checked = getBooleanValueFromAriaAttribute(ariaSelected);
    const disabled = getBooleanValueFromAriaAttribute(ariaDisabled);
    const state = { checked, disabled };
    return (
      <MenuItem
        {...optionProps}
        {...state}
        tabIndex={0}
        ref={(el) => {
          optionRefs.current[index] = el;
        }}
        onKeyDown={(event) => handleOptionKeyDown(event, index)}
      >
        {multiple ? renderCheckbox(option, state) : null}
        {renderOption(optionProps, option)}
      </MenuItem>
    );
  };

  const renderGroup = (option: AutocompleteGroupedOption<T>): JSX.Element => (
    <li key={option.key}>
      <ListSubheader
        data-testId={option.group
          .toLowerCase()
          .replace(' ', '-')
          .concat('-option-group')}
      >
        {`${option.group} (${option.options.length})`}
      </ListSubheader>
      <ul>
        {option.options.map((option2, index2) =>
          renderListOption(option2, option.index + index2),
        )}
      </ul>
    </li>
  );

  return (
    <div {...getRootProps()} style={{ position: 'relative' }}>
      <div ref={setAnchorEl} data-testid='listbox-search'>
        <TextField
          // If `openProp` is specified is means the consumer is already handing another Popper
          // In that case, we should add some margin for spacing purposes
          {...TextFieldProps}
          sx={{
            margin: openProp ? 1 : 0,
            ...TextFieldProps?.sx,
          }}
          InputLabelProps={{
            ...getInputLabelProps(),
            ...TextFieldProps?.InputLabelProps,
          }}
          inputProps={{
            ...getInputProps(),
            ...TextFieldProps?.inputProps,
            readOnly,
            maxLength,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              handleInputKeyDown(event, open);
              TextFieldProps?.inputProps?.onKeyDown?.(event);
            },
          }}
        />
      </div>
      {open && anchorEl ? (
        <ListContainer
          ref={dropdownContainerRef}
          style={{
            minWidth: anchorEl?.clientWidth,
            padding: 8,
          }}
          data-testid='listbox-container'
        >
          {loading || groupedOptions.length === 0 ? (
            <Box textAlign='center'>
              {loading ? (
                <CircularProgress />
              ) : (
                <Typography>{noOptionsText}</Typography>
              )}
            </Box>
          ) : (
            <ul
              ref={listboxRef}
              {...getListboxProps()}
              {...listboxProps}
              onScroll={handleScroll}
            >
              {groupedOptions.map((option, index) => {
                if (groupBy) {
                  return renderGroup(option as AutocompleteGroupedOption<T>);
                }
                return renderListOption(option as T, index);
              })}
            </ul>
          )}

          <ExtendedAutocompleteActions
            ClearButtonProps={{ ...ClearButtonProps, onClick: handleClear }}
            ApplyButtonProps={{
              ...ApplyButtonProps,
              onClick: multiple ? handleApply : undefined,
            }}
            CancelButtonProps={{ ...CancelButtonProps, onClick: handleCancel }}
          />
        </ListContainer>
      ) : null}
    </div>
  );
}
